import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { errorHelper, Loader } from '../../utils/tools';
import { formValues, validation } from './validationSchema';

// Redux
import { getBranchById, editBranch } from '../../store/actions/branch';

const EditBranch = ({ branchId, onClose }) => {
    const dispatch = useDispatch();

    // Fetch branch details when component mounts
    useEffect(() => {
        dispatch(getBranchById(branchId));
    }, [branchId, dispatch]);

    // Redux selectors
    const notifications = useSelector(state => state.notifications);
    const branchToEdit = useSelector(state => state.branch.branchToEdit);
    const loading = useSelector(state => state.branch.branchToEditLoading);

    // Submit handler
    const handleSubmit = (values) => {
        dispatch(editBranch({branchId, values}));
    };

    // Initialize formik with initial values
    const formik = useFormik({
        initialValues: formValues,
        validationSchema: validation,
        onSubmit: (values) => {
            handleSubmit(values);
        },
        validateOnChange: true,
        validateOnBlur: true,
    });

    // Update formik values once branchToEdit is loaded
    useEffect(() => {
        if (branchToEdit) {
            formik.setValues({
                name: branchToEdit.name || '',
                contact: branchToEdit.contact || '',
                street: branchToEdit.address.street || '',
                buildingNumber: branchToEdit.address.buildingNumber || '',
                registrationNumber: branchToEdit.address.registrationNumber || '',
                zip: branchToEdit.address.zip || '',
                city: branchToEdit.address.city || '',
                responsiblePerson: branchToEdit.responsiblePerson || '',
            });
        }
    }, [branchToEdit]); // Runs every time branchToEdit changes

    // Reset form on success notification
    useEffect(() => {
        if (notifications && notifications.global.success) {
            formik.resetForm();
            onClose();
        }
    }, [notifications, formik]);

    const renderLoader = () => (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '75vh' 
        }}>
            <Loader />
        </div>
    );

    if (loading) return renderLoader();

    return (
        <Form onSubmit={formik.handleSubmit}>
            <h2>Upraviť prevádzku</h2>
            <hr />

            <Row>
                <Form.Group className="mb-3" controlId="formBasicName">
                    <Form.Label>Názov prevádzky</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Názov prevádzky:"
                        {...formik.getFieldProps('name')}
                        isInvalid={formik.touched.name && formik.errors.name}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errorHelper(formik, 'name').helperText}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formBasicContact">
                    <Form.Label>Mobil</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Mobil:"
                        {...formik.getFieldProps('contact')}
                        isInvalid={formik.touched.contact && formik.errors.contact}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errorHelper(formik, 'contact').helperText}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formBasicStreet">
                    <Form.Label>Ulica</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Ulica:"
                        {...formik.getFieldProps('street')}
                        isInvalid={formik.touched.street && formik.errors.street}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errorHelper(formik, 'street').helperText}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formBasicBuildingNumber">
                    <Form.Label>Orientačné číslo</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Orientačné číslo:"
                        {...formik.getFieldProps('buildingNumber')}
                        isInvalid={formik.touched.buildingNumber && formik.errors.buildingNumber}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errorHelper(formik, 'buildingNumber').helperText}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formBasicRegistrationNumber">
                    <Form.Label>Súpisné číslo</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Súpisné číslo:"
                        {...formik.getFieldProps('registrationNumber')}
                        isInvalid={formik.touched.registrationNumber && formik.errors.registrationNumber}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errorHelper(formik, 'registrationNumber').helperText}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formBasicZip">
                    <Form.Label>PSČ</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="PSČ:"
                        {...formik.getFieldProps('zip')}
                        isInvalid={formik.touched.zip && formik.errors.zip}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errorHelper(formik, 'zip').helperText}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formBasicCity">
                    <Form.Label>Mesto</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Mesto:"
                        {...formik.getFieldProps('city')}
                        isInvalid={formik.touched.city && formik.errors.city}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errorHelper(formik, 'city').helperText}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formBasicResponsiblePerson">
                    <Form.Label>Zodpovedná osoba (meno a priezvisko)</Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Meno a priezvisko:"
                        {...formik.getFieldProps('responsiblePerson')}
                        isInvalid={formik.touched.responsiblePerson && formik.errors.responsiblePerson}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errorHelper(formik, 'responsiblePerson').helperText}
                    </Form.Control.Feedback>
                </Form.Group>

            </Row>

            <Row className="mt-auto">
                <hr />
                <Col className="d-flex justify-content-end">
                    <Button 
                        className='button-custom-large' 
                        variant='primary' 
                        type='submit' 
                        size="lg" 
                        style={{ marginTop: 'auto' }}
                    >
                        Upraviť prevádzku
                    </Button>
                </Col>
            </Row>
        </Form>
    )   
};

export default EditBranch;
